import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pendingOffersApi } from '../../api/pendingOffers';
import { entitiesApi } from '../../api/entities';
import { OFFER_MENU_TYPES, offerKindLabel } from '../../utils/chatIdeaOffers';
import {
  ADD_TO_BOOK_NOTES,
  CAST_NOT_NOW,
  DRAFT_SAVED,
  IDEA_OFFER_DISMISS_FAILED,
  IDEA_OFFER_SAVE_FAILED,
  OFFER_BLOCKED_UNDO,
  OFFER_NOT_A_NOTE,
  offerBlockedLine,
  offerChangeTypeLabel,
} from '../../utils/writerCopy';
import './ChatIdeaOffers.css';

/** The Undo line took the focus; give it back to the restored card's type button. */
export function focusOfferTypeButton(id) {
  const key = String(id || '');
  if (!key || typeof document === 'undefined') return;
  const el = Array.from(document.querySelectorAll('[data-offer-type-id]'))
    .find((node) => node.getAttribute('data-offer-type-id') === key);
  if (el) el.focus();
}

export function isConflict(err) {
  return Boolean(err) && (err.status === 409 || err.response?.status === 409);
}

/** "Okay, X won't come up again" plus Undo. Not a toast. Takes focus on Undo when it appears. */
export function OfferBlockedLine({ name, onUndo, disabled = false }) {
  const undoRef = useRef(null);
  useEffect(() => {
    if (undoRef.current) undoRef.current.focus();
  }, []);
  return (
    <p className="idea-offers__blocked" data-testid="offer-blocked-line">
      <span>{offerBlockedLine(name)}</span>
      <button
        type="button"
        ref={undoRef}
        className="idea-offers__undo"
        disabled={disabled}
        onClick={onUndo}
      >
        {OFFER_BLOCKED_UNDO}
      </button>
    </p>
  );
}

/** The type word as a button. Opens Person / Place / Thing / Event, a divider, then the don't-offer item. */
function OfferTypeMenu({ offer, type, disabled, onPick, onBlock }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const typeLabel = offerKindLabel(type);
  const checkedIndex = Math.max(0, OFFER_MENU_TYPES.indexOf(type));
  const lastIndex = OFFER_MENU_TYPES.length;

  // The menu is portaled to body with position: fixed, so only the viewport limits it.
  // Open toward the side with more room, anchored to the button.
  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      setMaxHeight(null);
      setPos(null);
      return;
    }
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button || !menu) return;
    const anchor = button.getBoundingClientRect();
    const below = window.innerHeight - anchor.bottom;
    const above = anchor.top;
    const up = above > below;
    const width = menu.offsetWidth || 200;
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
    setOpenUp(up);
    setMaxHeight(Math.max(0, (up ? above : below) - 8));
    setPos(up ? { bottom: window.innerHeight - anchor.top + 4, left } : { top: anchor.bottom + 4, left });
  }, [open]);

  // A fixed menu must not drift from its button: close on resize or any scroll outside the menu.
  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => setOpen(false);
    const onScroll = (event) => {
      const target = event.target;
      if (target instanceof Node && menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const focusItem = (node) => {
    if (!node) return;
    node.focus();
    if (typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
  };

  useEffect(() => {
    if (!open) return undefined;
    focusItem(itemRefs.current[checkedIndex]);
    const onDown = (event) => {
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      const onButton = buttonRef.current && buttonRef.current.contains(event.target);
      if (!inMenu && !onButton) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeToButton = () => {
    setOpen(false);
    if (buttonRef.current) buttonRef.current.focus();
  };

  const onButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!disabled) setOpen(true);
    }
  };

  const onMenuKeyDown = (event) => {
    const items = itemRefs.current;
    const at = items.findIndex((node) => node === document.activeElement);
    let next = null;
    if (event.key === 'ArrowDown') next = at < 0 ? 0 : (at + 1) % (lastIndex + 1);
    else if (event.key === 'ArrowUp') next = at <= 0 ? lastIndex : at - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = lastIndex;
    else if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      closeToButton();
      return;
    }
    if (next === null) return;
    event.preventDefault();
    focusItem(items[next]);
  };

  return (
    <span className="idea-offers__menu-wrap">
      <button
        type="button"
        ref={buttonRef}
        className="idea-offers__kind idea-offers__kind-button"
        data-offer-type-id={String(offer.id)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={offerChangeTypeLabel(offer.name, typeLabel)}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onButtonKeyDown}
      >
        {typeLabel}
      </button>
      {open ? createPortal(
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        <div
          className={`idea-offers__menu idea-offers__menu--fixed${openUp ? ' idea-offers__menu--up' : ''}`}
          role="menu"
          ref={menuRef}
          style={maxHeight === null ? undefined : { ...pos, maxHeight: `${maxHeight}px` }}
          onKeyDown={onMenuKeyDown}
        >
          {OFFER_MENU_TYPES.map((value, index) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={value === type}
              tabIndex={-1}
              ref={(node) => { itemRefs.current[index] = node; }}
              className="idea-offers__menu-item"
              disabled={disabled}
              onClick={() => { closeToButton(); if (value !== type) onPick(value); }}
            >
              <span className="idea-offers__check" aria-hidden="true">{value === type ? '✓' : ''}</span>
              {offerKindLabel(value)}
            </button>
          ))}
          <div role="separator" className="idea-offers__menu-divider" />
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            ref={(node) => { itemRefs.current[lastIndex] = node; }}
            className="idea-offers__menu-item"
            disabled={disabled}
            onClick={() => { setOpen(false); onBlock(); }}
          >
            <span className="idea-offers__check" aria-hidden="true" />
            {OFFER_NOT_A_NOTE}
          </button>
        </div>,
        document.body,
      ) : null}
    </span>
  );
}

/**
 * One card per idea, open under the reply. Each idea is a button pair:
 * save it as a Note, or dismiss it. Not words inside the reply.
 * Not now uses the same pending-offer reject as Notes.
 * #2573: the type word opens a menu to change the type or stop offering the name.
 * With onBlocked the parent shows the Undo line (the cards may be gone by then);
 * without it this component keeps the line itself.
 */
export default function ChatIdeaOffers({
  offers,
  storyId,
  onResolved,
  onBlocked = null,
  trailing = null,
  blockedFor = [],
  onUndoBlocked = null,
  blockedBusy = false,
  acceptOffer = (storyIdArg, body) => pendingOffersApi.accept(storyIdArg, body),
  rejectOffer = (storyIdArg, body) => pendingOffersApi.reject(storyIdArg, body),
  blockOffer = (storyIdArg, body) => pendingOffersApi.reject(storyIdArg, { ...body, block: true }),
  changeType = (entityId, entityType) => entitiesApi.setType(entityId, entityType),
  unblockOffer = (entityId) => entitiesApi.unblock(entityId),
}) {
  const [busyId, setBusyId] = useState('');
  const [savedIds, setSavedIds] = useState([]);
  const [typeById, setTypeById] = useState({});
  const [blockedLines, setBlockedLines] = useState([]);
  const [error, setError] = useState('');
  const busyRef = useRef('');
  const list = offers || [];
  if (!list.length && !blockedLines.length && !blockedFor.length) return trailing || null;

  const save = async (offer) => {
    const description = String(offer.quoted_text || offer.name || '').trim();
    const id = String(offer.id || '');
    if (!storyId || !description || !id) {
      setError(IDEA_OFFER_SAVE_FAILED);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = id;
    setBusyId(id);
    setError('');
    let ok = false;
    try {
      await acceptOffer(storyId, { kind: 'chat', id: offer.id });
      ok = true;
    } catch (err) {
      setError(IDEA_OFFER_SAVE_FAILED);
    } finally {
      busyRef.current = '';
      setBusyId('');
    }
    if (!ok) return;
    setSavedIds((prev) => (prev.includes(id) ? prev : prev.concat(id)));
    onResolved?.(offer.id);
  };

  const dismiss = async (offer) => {
    const id = String(offer.id || '');
    if (!storyId || !id) {
      setError(IDEA_OFFER_DISMISS_FAILED);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = id;
    setBusyId(id);
    setError('');
    try {
      await rejectOffer(storyId, {
        kind: 'chat',
        id: offer.id,
        source_ref: offer.source_ref || null,
      });
      onResolved?.(offer.id);
    } catch (err) {
      setError(IDEA_OFFER_DISMISS_FAILED);
    } finally {
      busyRef.current = '';
      setBusyId('');
    }
  };

  const pickType = async (offer, entityType) => {
    const id = String(offer.id || '');
    if (!id || busyRef.current) return;
    busyRef.current = id;
    setBusyId(id);
    setError('');
    try {
      await changeType(offer.id, entityType);
      setTypeById((prev) => ({ ...prev, [id]: entityType }));
    } catch (err) {
      setError(IDEA_OFFER_SAVE_FAILED);
    } finally {
      busyRef.current = '';
      setBusyId('');
    }
  };

  const block = async (offer) => {
    const id = String(offer.id || '');
    if (!storyId || !id) {
      setError(IDEA_OFFER_DISMISS_FAILED);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = id;
    setBusyId(id);
    setError('');
    let ok = false;
    try {
      await blockOffer(storyId, {
        kind: 'chat',
        id: offer.id,
        source_ref: offer.source_ref || null,
      });
      ok = true;
    } catch (err) {
      setError(IDEA_OFFER_DISMISS_FAILED);
    } finally {
      busyRef.current = '';
      setBusyId('');
    }
    if (!ok) return;
    const key = String(offer.name || '').trim().toLowerCase();
    list
      .filter((row) => String(row.name || '').trim().toLowerCase() === key)
      .forEach((row) => onResolved?.(row.id));
    if (onBlocked) onBlocked(offer);
    else setBlockedLines((prev) => prev.concat({ id, name: offer.name }));
  };

  const undo = async (line) => {
    setError('');
    try {
      await unblockOffer(line.id);
    } catch (err) {
      if (!isConflict(err)) {
        setError(IDEA_OFFER_DISMISS_FAILED);
        return;
      }
    }
    setBlockedLines((prev) => prev.filter((row) => row.id !== line.id));
  };

  return (
    <div className="idea-offers" data-testid="idea-offers">
      <ul className="idea-offers__list">
        {list.map((offer) => {
          const busy = busyId === String(offer.id);
          const saved = Boolean(offer.offer_saved) || savedIds.includes(String(offer.id));
          const type = typeById[String(offer.id)] || offer.entity_type;
          return (
            <li
              key={`${String(offer.id)}:${offer.entity_type}:${offer.name}`}
              className="idea-offers__item"
              data-testid="idea-offer"
            >
              <span className="idea-offers__name">{offer.name}</span>
              {saved ? (
                <span className="idea-offers__kind">{offerKindLabel(type)}</span>
              ) : (
                <OfferTypeMenu
                  offer={offer}
                  type={String(type || '').toLowerCase()}
                  disabled={busy}
                  onPick={(next) => { void pickType(offer, next); }}
                  onBlock={() => { void block(offer); }}
                />
              )}
              {saved ? (
                <span className="idea-offers__saved">{DRAFT_SAVED}</span>
              ) : (
              <>
              <button
                type="button"
                className="idea-offers__save"
                disabled={busy}
                onClick={() => { void save(offer); }}
              >
                {ADD_TO_BOOK_NOTES}
              </button>
              <button
                type="button"
                className="idea-offers__dismiss"
                disabled={busy}
                onClick={() => { void dismiss(offer); }}
              >
                {CAST_NOT_NOW}
              </button>
              </>
              )}
            </li>
          );
        })}
        {trailing ? <li className="idea-offers__extra">{trailing}</li> : null}
      </ul>
      <div aria-live="polite">
        {blockedLines.map((line) => (
          <OfferBlockedLine key={line.id} name={line.name} onUndo={() => { void undo(line); }} />
        ))}
        {blockedFor.map((line) => (
          <OfferBlockedLine
            key={line.id}
            name={line.name}
            disabled={blockedBusy}
            onUndo={() => { if (onUndoBlocked) void onUndoBlocked(line); }}
          />
        ))}
      </div>
      {error ? <p className="idea-offers__error" role="alert">{error}</p> : null}
    </div>
  );
}
